<template>
  <div class="registration-detail" :class="{ 'pa-10': !inOrganization }">
    <v-breadcrumbs
        v-if="inOrganization"
        class="mt-3 level-2"
        :items="[
          { text: $tc('member.member', 2), to: { name: 'organizations.members' }, exact: true },
          { text: displayName || $t('onboarding.registration.file') },
        ]"
        divider=">"
    />

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

    <v-skeleton-loader v-if="loading && !registration" type="card, card" />

    <template v-else-if="registration">
      <!-- Qui, et où en est son dossier, d'un coup d'œil -->
      <v-card class="mb-4">
        <v-card-text class="d-flex align-center flex-wrap" style="gap: 16px">
          <v-avatar color="primary" size="48" class="white--text font-weight-bold">{{ initials }}</v-avatar>
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-bold">{{ displayName }}</div>
            <div class="text--secondary">
              {{ registration.email }}
              <template v-if="registration.applicant && registration.applicant.phone"> · {{ registration.applicant.phone }}</template>
              <template v-if="registration.applicant && registration.applicant.externalReference"> · {{ $t('member.external-reference') }} {{ registration.applicant.externalReference }}</template>
            </div>
          </div>
          <div class="text-right">
            <v-chip small :color="statusColor" dark>{{ $t(`onboarding.registration.status.${registration.status}`) }}</v-chip>
            <div class="text-caption text--secondary mt-1">
              {{ $t(`onboarding.registration.source.${registration.source}`) }} · {{ registration.createdAt | formatDate }}
            </div>
          </div>
        </v-card-text>

        <!-- Le parcours : ce qui est fait, ce qui se passe maintenant, ce qui suit -->
        <v-divider />
        <v-card-text>
          <div class="journey">
            <div v-for="(stage, index) in journey" :key="stage.key" class="journey__stage" :class="`journey__stage--${stage.state}`">
              <div class="journey__line" v-if="index > 0" />
              <div class="journey__dot">
                <v-icon v-if="stage.state === 'done'" x-small color="white">mdi-check</v-icon>
                <v-icon v-else-if="stage.state === 'failed'" x-small color="white">mdi-exclamation</v-icon>
              </div>
              <div class="journey__label">{{ $t(`onboarding.registration.journey.${stage.key}`) }}</div>
              <div class="journey__detail">{{ stage.detail }}</div>
            </div>
          </div>
        </v-card-text>

        <!-- Ce qu'on attend, et de qui : toujours dit, avec le geste quand on peut le faire ici. -->
        <v-card-text class="pt-0">
          <v-alert :color="expected.color" :type="expected.type" text class="mb-0">
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <div class="mr-4">
                <b>{{ $t('onboarding.registration.expected.title') }} :</b> {{ expected.text }}
                <div v-if="expected.remind" class="text-caption text--secondary">
                  <template v-if="registration.invitedAt">
                    {{ $t('onboarding.registration.invitation.sent-on', { date: $options.filters.formatDate(registration.invitedAt) }) }}
                  </template>
                  <template v-else>{{ $t('onboarding.registration.invitation.never-sent') }}</template>
                </div>
              </div>
              <v-spacer />
              <registration-approval-actions v-if="expected.approve" :registration-id="registration.id" @decided="load" />
              <template v-if="expected.remind && canIssueLink">
                <v-btn small outlined color="primary" :loading="sendingInvitation" @click="sendInvitation">
                  <v-icon left small>mdi-email-fast-outline</v-icon>
                  {{ $t(registration.invitedAt ? 'onboarding.registration.invitation.resend' : 'onboarding.registration.invitation.send') }}
                </v-btn>
                <v-btn small text color="primary" :loading="issuingLink" @click="issueLink">
                  <v-icon left small>mdi-link-variant</v-icon>
                  {{ $t('onboarding.registration.link.issue') }}
                </v-btn>
              </template>
              <registration-cancel-menu
                  v-if="canCancel"
                  as-button
                  :registration-id="registration.id"
                  :name="displayName"
                  :order-id="linkedOrderId"
                  @cancelled="load"
              />
              <v-btn v-if="expected.provision" small color="primary" :loading="provisioning" @click="provision">
                <v-icon left small>mdi-play</v-icon>
                {{ plan && (plan.status === 'failed' || planStalled) ? $t('onboarding.registration.resume') : $t('onboarding.registration.provision') }}
              </v-btn>
              <v-btn v-if="expected.member && member" small text color="primary" target="_blank" :href="$router.resolve({ name: 'member', params: { memberId: member.id } }).href">
                {{ $t('onboarding.registration.expected.see-member') }}
              </v-btn>
            </div>
          </v-alert>
          <v-alert v-if="registration.status === 'rejected' && registration.closingReason" type="warning" dense text class="mt-3 mb-0">
            {{ $t('onboarding.registration.approval.rejected-because', { reason: registration.closingReason }) }}
          </v-alert>
        </v-card-text>
      </v-card>

      <v-row>
        <!-- Ce que la personne a renseigné -->
        <v-col cols="12" md="6">
          <v-card class="fill-height">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.registration.applicant-card') }}</v-card-title>
            <v-card-text>
              <div v-if="!registration.applicantComplete" class="text--secondary">{{ $t('onboarding.registration.applicant-empty') }}</div>
              <template v-else>
                <div class="text-caption text--secondary">{{ $t('onboarding.registration.postal-address') }}</div>
                <div class="mb-3">{{ postalAddress }}</div>
                <div v-for="field in registration.customFields || []" :key="field.id" class="mb-2">
                  <div class="text-caption text--secondary">{{ field.label }}</div>
                  <div>{{ field.type === 'bool' ? $t(field.value ? 'yes' : 'no') : field.value }}</div>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Le moyen de paiement donné, et les équipements annoncés -->
        <v-col cols="12" md="6">
          <v-card class="fill-height">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.registration.payment-card') }}</v-card-title>
            <v-card-text>
              <div v-if="paymentLabel" class="d-flex align-center">
                <v-icon class="mr-2" color="success">{{ registration.paymentMethod.instrument === 'card' ? 'mdi-credit-card-check-outline' : 'mdi-bank-check' }}</v-icon>
                <div>
                  <div>{{ paymentLabel }}</div>
                  <div v-if="registration.paymentMethod.at" class="text-caption text--secondary">
                    {{ $t('onboarding.registration.payment-given-on', { date: $options.filters.formatDate(registration.paymentMethod.at) }) }}
                  </div>
                </div>
              </div>
              <div v-else-if="registration.requiredGates.includes('payment-method')" class="text--secondary">{{ $t('onboarding.registration.payment-awaited') }}</div>
              <div v-else class="text--secondary">{{ $t('onboarding.registration.payment-not-required') }}</div>

              <div class="text-caption text--secondary mt-4 mb-1">{{ $t('onboarding.configuration.setting.membership.equipment') }}</div>
              <template v-if="(registration.equipment || []).length">
                <v-chip v-for="kind in registration.equipment" :key="kind" small class="mr-2">
                  {{ $t(`onboarding.commissioning.kind.${kind}`) }}
                </v-chip>
              </template>
              <span v-else>{{ $t('onboarding.registration.no-equipment') }}</span>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ce que la commande d'office n'a pas pu passer à son arrivée : dit ici, avec la raison, et où le commander. -->
      <v-alert v-if="(registration.announcedRefusals || []).length" type="warning" text dense class="mt-4 mb-0">
        <div class="font-weight-bold mb-1">{{ $t('onboarding.registration.announced-refused.title') }}</div>
        <div v-for="refusal in registration.announcedRefusals" :key="refusal.kind" class="text-body-2">
          {{ $t(`onboarding.commissioning.kind.${refusal.kind}`) }} : {{ refusal.reason }}
          <span class="text--secondary">({{ $options.filters.formatDate(refusal.at) }})</span>
        </div>
        <div class="d-flex align-center flex-wrap mt-2" style="gap: 8px">
          <span class="text-caption">{{ $t('onboarding.registration.announced-refused.explained') }}</span>
          <v-btn v-if="member" small outlined color="primary" target="_blank" :href="$router.resolve({ name: 'member', params: { memberId: member.id } }).href">
            {{ $t('onboarding.registration.announced-refused.open-member') }}
          </v-btn>
        </div>
      </v-alert>

      <!-- Les commandes nées de cette adhésion, une fois le membre actif. -->
      <equipment-orders-card
          v-if="registration.status === 'active' && (registration.equipment || []).length"
          class="mt-4"
          :organization-id="registration.organizationId"
          :title="$t('onboarding.registration.orders')"
          :query="ordersQuery"
          :matches="isBornHere"
          holder="member"
          :orderable="false"
      />

      <!-- Le détail, pour qui en a besoin : les gestes de la création du compte, les réglages appliqués. -->
      <v-expansion-panels v-model="openPanels" multiple class="mt-4">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              {{ $t('onboarding.registration.plan') }}
              <v-chip v-if="plan" x-small class="ml-2" :color="planColor" dark>
                {{ $t(`onboarding.registration.plan-status.${plan.status}`) }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="!plan" class="text--secondary">{{ $t('onboarding.registration.no-plan') }}</div>
            <template v-else>
              <p v-if="plan.status === 'prepared'" class="text-caption text--secondary">{{ $t('onboarding.registration.plan-prepared') }}</p>
              <v-simple-table dense>
                <template #default>
                  <tbody>
                    <tr v-for="(step, index) in plan.steps" :key="step.name">
                      <td style="width: 40px">{{ index + 1 }}</td>
                      <td style="width: 40px">
                        <v-icon small :color="stepColor(step)">{{ stepIcon(step) }}</v-icon>
                      </td>
                      <td>
                        {{ stepName(step) }}
                        <!-- Un échec porte sa raison et ses valeurs : c'est ce qui dit quoi corriger. -->
                        <div v-if="step.reason" class="text-caption red--text">
                          {{ step.reasonLabel || $t(step.reason, step.reasonParameters || {}) }}
                        </div>
                      </td>
                      <td class="text-right text-caption text--secondary" style="white-space: nowrap">
                        {{ step.executedAt | formatDate }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>{{ $t('onboarding.registration.settings') }}</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex justify-end">
              <v-switch v-model="onlyDecided" dense hide-details class="mt-0 pt-0 mb-2" :label="$t('onboarding.registration.only-decided')" />
            </div>
            <v-simple-table dense>
              <template #default>
                <thead>
                  <tr>
                    <th>{{ $t('onboarding.registration.setting') }}</th>
                    <th>{{ $t('onboarding.registration.value') }}</th>
                    <th>{{ $t('onboarding.registration.provenance') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in visibleSettings" :key="row.key">
                    <td>{{ labelOf(row.key) }}</td>
                    <td>{{ render(row.key, row.value) }}</td>
                    <td>
                      <v-chip x-small :color="provenanceColor(row.from)" :dark="row.from !== 'default'">
                        {{ $t(`onboarding.registration.from.${row.from}`) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <!-- Le lien ne s'affiche qu'ici, une fois : seule son empreinte est gardée. -->
    <v-dialog v-model="link.open" max-width="520">
      <v-card>
        <v-card-title>{{ $t('onboarding.registration.link.title') }}</v-card-title>
        <v-card-text>
          <p class="text-caption">{{ $t('onboarding.registration.link.explained', { date: $options.filters.formatDate(link.expiresAt) }) }}</p>
          <qr-link-actions :link="link.url" show-link filename="invitation-link.png" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="link.open = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { planStepName } from './planLabels'
import RegistrationRepository from '@repository/RegistrationRepository'
import OnboardingSettingsMixin from './OnboardingSettingsMixin'
import QrLinkActions from '@/components/shared/QrLinkActions.vue'
import EquipmentOrdersCard from './Commissioning/EquipmentOrdersCard.vue'
import RegistrationMixin from './RegistrationMixin'
import RegistrationCancelMenu from './Members/RegistrationCancelMenu.vue'
import RegistrationApprovalActions from './Registration/RegistrationApprovalActions.vue'

export default {
  name: 'RegistrationDetail',

  components: { EquipmentOrdersCard, QrLinkActions, RegistrationApprovalActions, RegistrationCancelMenu },

  mixins: [OnboardingSettingsMixin, RegistrationMixin],

  data() {
    return {
      registration: null,
      loading: false,
      provisioning: false,
      issuingLink: false,
      sendingInvitation: false,
      link: { open: false, url: null, expiresAt: null },
      error: null,
      // Par défaut on ne montre que ce que quelqu'un a décidé : la liste complète des réglages
      // par défaut noie ce qui distingue vraiment ce dossier-là.
      onlyDecided: true,
      // Le détail technique reste replié, sauf quand il dit pourquoi la création du compte s'est arrêtée.
      openPanels: [],
    }
  },

  computed: {
    /** Le dossier s'ouvre dans l'organisation (onglets, fil d'Ariane) ; l'ancienne adresse y renvoie. */
    inOrganization() {
      return this.$route.name === 'organizations.members.registration'
    },

    // Gardé en cache sous l'organisation (keep-alive), l'écran voit aussi les routes des autres pages : leur `id`
    // n'est pas un dossier.
    registrationId() {
      if (this.inOrganization) {
        return this.$route.params.registrationId
      }
      return this.$route.name === 'onboarding.registrations.detail' ? this.$route.params.id : null
    },

    initials() {
      return (this.displayName || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('')
    },

    postalAddress() {
      return this.registrationAddress(this.registration)
    },

    paymentLabel() {
      return this.paymentMethodLabel(this.registration?.paymentMethod)
    },

    /**
     * Le parcours de la personne, de l'invitation au membre actif : ce qui est fait (avec sa date ou son détail), l'étape
     * en cours, ce qui suit. Les étapes qui ne s'appliquent pas à ce dossier (moyen de paiement, approbation) n'y sont pas.
     */
    journey() {
      const r = this.registration
      if (!r) return []
      const date = d => (d ? this.$options.filters.formatDate(d) : '')
      const passed = gate => (r.passedGates || []).includes(gate)
      const closed = ['cancelled', 'rejected', 'expired'].includes(r.status)
      const stages = [
        { key: r.source === 'invitation-link' ? 'joined' : 'invited', done: r.status !== 'draft' || !!r.invitedAt, detail: date(r.invitedAt || r.createdAt) },
        { key: 'applicant', done: !!r.applicantComplete },
      ]
      if ((r.requiredGates || []).includes('payment-method')) stages.push({ key: 'payment-method', done: passed('payment-method'), detail: this.paymentLabel })
      if (r.requiresApproval) stages.push({ key: 'approval', done: passed('approval') })
      stages.push(
        { key: 'validated', done: ['validated', 'created', 'active'].includes(r.status), detail: date(r.validatedAt) },
        { key: 'account', done: ['created', 'active'].includes(r.status), failed: this.plan?.status === 'failed' },
        { key: 'active', done: r.status === 'active' },
      )
      let current = closed
      // Validé sans elles (dossier créé par l'organisation, sans moyen de paiement demandé…) : les étapes d'avant sont
      // passées, pas en attente.
      const beyond = ['validated', 'created', 'active'].includes(r.status)
      const validatedAt = stages.findIndex(stage => stage.key === 'validated')
      return stages.map((stage, index) => {
        if (!stage.done && beyond && index < validatedAt) return { ...stage, state: 'skipped', detail: this.$t('onboarding.registration.journey.skipped') }
        let state = stage.done ? 'done' : 'todo'
        if (!stage.done && stage.failed) {
          state = 'failed'
          current = true
        } else if (!stage.done && !current) {
          state = 'current'
          current = true
        }
        return { ...stage, state }
      })
    },

    plan() {
      return this.registration?.plan || null
    },

    displayName() {
      return this.registration ? this.registrationName(this.registration) : null
    },

    /** L'action attendue selon l'état du dossier : quoi, de qui, et le geste s'il se fait d'ici. */
    expected() {
      const i = this.registration
      const t = key => this.$t(`onboarding.registration.expected.${key}`)
      switch (i.status) {
        case 'awaiting-approval':
          if (!i.applicantComplete) return { type: 'info', text: t('applicant-first'), remind: true }
          return this.mayApproveMembers
            ? { type: 'warning', text: t('approve'), approve: true }
            : { type: 'warning', text: t('approver-needed') }
        case 'draft':
        case 'invited':
        case 'in-progress':
          return { type: 'info', text: t('applicant'), remind: true }
        case 'awaiting-payment-method':
          return { type: 'info', text: t('payment-method'), remind: true }
        case 'validated':
        case 'created':
          // Un geste a échoué : ce n'est plus « rien à faire ». On dit pourquoi, et qu'il faut reprendre.
          if (this.failedStep) return { type: 'error', text: this.$t('onboarding.registration.expected.provisioning-failed', { reason: this.failedStep.reasonLabel || this.$t(this.failedStep.reason, this.failedStep.reasonParameters || {}) }), provision: this.canProvision }
          // En cours, mais plus rien ne bouge depuis un moment : le job s'est arrêté, on le dit et on propose de reprendre.
          if (this.planStalled) return { type: 'warning', text: this.$t('onboarding.registration.expected.provisioning-stalled', { date: this.$options.filters.formatDate(this.plan.lastActivityAt) }), provision: this.canProvision }
          // Pas encore démarrée : elle part d'elle-même, le bouton ne sert qu'à ne pas l'attendre.
          if (this.canProvision && this.plan?.status === 'prepared') return { type: 'info', text: t('provisioning-prepared'), provision: true }
          return { type: 'info', text: t('provisioning') }
        case 'active':
          // Un équipement annoncé que la commande d'office a refusé : il reste à le commander à la main.
          if ((i.announcedRefusals || []).length) {
            const kinds = i.announcedRefusals.map(r => this.$t(`onboarding.commissioning.kind.${r.kind}`).toLowerCase()).join(', ')
            return { type: 'warning', text: this.$t('onboarding.registration.expected.active-to-order', { kinds }), member: true }
          }
          // Déjà membre avant ce dossier : il a rejoint son compte existant, rien de nouveau n'est apparu.
          return { type: 'success', text: this.member?.existing ? t('active-existing-member') : t('active'), member: true }
        default:
          return { type: 'info', color: 'grey', text: t('closed') }
      }
    },

    /** Le membre du dossier, tel que le geste de rattachement l'a retrouvé ou créé. */
    member() {
      const step = (this.registration?.plan?.steps || []).find(s => s.name === 'attach-member')
      const id = step?.produced?.memberId
      return id ? { id, existing: step.outcome === 'already-done' } : null
    },

    /** Le geste qui a arrêté la création du compte, s'il y en a un. */
    failedStep() {
      const plan = this.registration?.plan
      if (!plan || plan.status !== 'failed') return null
      return (plan.steps || []).find(step => step.outcome === 'failed' && step.reason) || null
    },

    /** Tant qu'elle n'est pas validée, une invitation s'annule (comme dans la liste des invitations). */
    canCancel() {
      return this.canIssueLink && (this.currentUser?.permissions || []).some(p => ['ONBOARDING_MODULE_MANAGE', 'ONBOARDING_INVITE'].includes(p))
    },

    /** La commande d'équipement qui attend cette inscription, annulée avec elle. */
    linkedOrderId() {
      const reference = this.registration?.businessReference || ''
      return reference.startsWith('equipment-order:') ? reference.slice('equipment-order:'.length) : null
    },

    /** Un lien n'a de sens que tant que la personne a quelque chose à remplir. */
    canIssueLink() {
      return ['draft', 'invited', 'in-progress', 'awaiting-approval', 'awaiting-payment-method']
        .includes(this.registration?.status)
    },

    /** Un plan « en cours » sans mouvement depuis cinq minutes s'est arrêté (le job a planté) : il se reprend. */
    planStalled() {
      if (this.plan?.status !== 'running' || !this.plan.lastActivityAt) return false
      return Date.now() - new Date(this.plan.lastActivityAt).getTime() > 5 * 60 * 1000
    },

    /** On ne lance que ce qui est validé ; un plan terminé ne se relance pas. */
    canProvision() {
      return this.registration?.status === 'validated'
    },

    statusColor() {
      return this.registrationStatusColor(this.registration?.status)
    },

    planColor() {
      if (this.plan?.status === 'completed') return 'green'
      if (this.plan?.status === 'failed') return 'red'
      if (this.plan?.status === 'running') return 'orange'
      return 'blue-grey'
    },

    settingRows() {
      const settings = this.registration?.settings || {}
      return Object.keys(settings).sort().map(key => ({
        key,
        value: settings[key].value,
        from: settings[key].from,
      }))
    },

    /** La recherche par e-mail resserre la liste ; seules comptent les commandes nées de ce dossier. */
    ordersQuery() {
      return { holder: 'member', search: this.registration?.email || null }
    },

    visibleSettings() {
      return this.onlyDecided
        ? this.settingRows.filter(row => row.from !== 'default')
        : this.settingRows
    },
  },

  created() {
    this.load()
  },

  watch: {
    registrationId(id) {
      if (id) {
        this.load()
      }
    },
  },

  methods: {
    stepName(step) {
      return planStepName(this, step)
    },

    async load() {
      this.loading = true
      this.error = null
      try {
        this.registration = await RegistrationRepository.get(this.registrationId)
        if (!this.inOrganization) {
          this.$router.replace({ name: 'organizations.members.registration', params: { id: this.registration.organizationId, registrationId: this.registration.id } })
          return
        }
        this.openPanels = this.registration.plan?.status === 'failed' ? [0] : []
        await this.loadPickers(this.registration.organizationId)
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('error-occurred')
      } finally {
        this.loading = false
      }
    },

    async provision() {
      this.provisioning = true
      this.error = null
      try {
        await RegistrationRepository.provision(this.registrationId)
        // On relit tout : le plan a bougé, et l'état du dossier avec lui.
        await this.load()
      } catch (e) {
        this.error = e?.response?.data?.message || this.$t('error-occurred')
      } finally {
        this.provisioning = false
      }
    },

    async sendInvitation() {
      this.sendingInvitation = true
      try {
        const result = await RegistrationRepository.sendInvitation(this.registrationId)
        this.notifySuccess('onboarding.registration.invitation.sent', { email: result.sentTo })
        await this.load()
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.sendingInvitation = false
      }
    },

    async issueLink() {
      this.issuingLink = true
      try {
        const result = await RegistrationRepository.issueLink(this.registrationId)
        const url = this.$router.resolve({ name: 'onboarding.tunnel', query: { token: result.token } }).href
        this.link = { open: true, url: `${window.location.origin}${url}`, expiresAt: result.expiresAt }
        await this.load()
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.issuingLink = false
      }
    },

    isBornHere(order) {
      return order.originReference === `registration:${this.registration.id}`
    },

    stepIcon(step) {
      if (step.outcome === 'done') return 'mdi-check-circle'
      if (step.outcome === 'already-done') return 'mdi-check-circle-outline'
      if (step.outcome === 'failed') return 'mdi-alert-circle'
      return 'mdi-circle-outline'
    },

    stepColor(step) {
      if (step.outcome === 'done') return 'green'
      if (step.outcome === 'already-done') return 'blue-grey'
      if (step.outcome === 'failed') return 'red'
      return 'grey lighten-1'
    },

    provenanceColor(from) {
      if (from === 'source') return 'deep-purple'
      if (from === 'configuration') return 'blue'
      return undefined
    },

  },
}
</script>

<style scoped>
/* Le parcours : une frise horizontale (verticale sur petit écran), une pastille par étape. */
.journey {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 0;
}
.journey__stage {
  position: relative;
  flex: 1 1 0;
  min-width: 120px;
  text-align: center;
  padding: 0 6px;
}
.journey__line {
  position: absolute;
  top: 11px;
  right: 50%;
  width: 100%;
  height: 2px;
  background: #d6d9de;
  z-index: 0;
}
.journey__stage--skipped .journey__line,
.journey__stage--done .journey__line,
.journey__stage--current .journey__line,
.journey__stage--failed .journey__line {
  background: #4caf50;
}
.journey__dot {
  position: relative;
  z-index: 1;
  width: 24px;
  height: 24px;
  margin: 0 auto 8px;
  border-radius: 50%;
  background: #d6d9de;
  display: flex;
  align-items: center;
  justify-content: center;
}
.journey__stage--done .journey__dot {
  background: #4caf50;
}
.journey__stage--skipped .journey__dot {
  background: #b0bec5;
}
.journey__stage--current .journey__dot {
  background: var(--v-primary-base);
  box-shadow: 0 0 0 5px rgba(229, 60, 34, 0.18);
}
.journey__stage--failed .journey__dot {
  background: #e53935;
}
.journey__label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}
.journey__stage--done .journey__label {
  color: rgba(0, 0, 0, 0.87);
}
.journey__stage--current .journey__label,
.journey__stage--failed .journey__label {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
}
.journey__detail {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 2px;
}
@media (max-width: 700px) {
  .journey {
    flex-direction: column;
  }
  .journey__stage {
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
  }
  .journey__line {
    display: none;
  }
  .journey__dot {
    margin: 0;
  }
}
</style>
